import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, X, Image as ImageIcon, Loader2 } from 'lucide-react';

type ProductForm = {
  name: string; brand: string; category: string; price: string; original_price: string;
  discount: string; description: string; in_stock: boolean;
  is_flash_sale: boolean; is_featured: boolean;
  stock_quantity: string; low_stock_threshold: string;
  colors: string; storages: string; rams: string;
};

type SpecificationRow = {
  key: string;
  value: string;
};

const empty: ProductForm = {
  name: '', brand: '', category: 'smartphones', price: '', original_price: '',
  discount: '0', description: '', in_stock: true, is_flash_sale: false, is_featured: false,
  stock_quantity: '0', low_stock_threshold: '5',
  colors: '', storages: '', rams: '',
};

const AdminProducts = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductForm>(empty);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [specifications, setSpecifications] = useState<SpecificationRow[]>([
    { key: '', value: '' }
  ]);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['admin', 'products'],
    enabled: !!user && isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Handle image file selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validate total images (existing + new)
    const totalImages = existingImages.length + imageFiles.length + files.length;
    if (totalImages > 3) {
      toast({
        title: 'Too many images',
        description: 'Maximum 3 images allowed per product.',
        variant: 'destructive',
      });
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: `${file.name} is not a valid image. Use JPG, PNG, or WebP.`,
          variant: 'destructive',
        });
        continue;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds 5MB limit.`,
          variant: 'destructive',
        });
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Add to state
    setImageFiles(prev => [...prev, ...validFiles]);

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove new image (not yet uploaded)
  const removeNewImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Remove existing image
  const removeExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  // Specification management
  const addSpecification = () => {
    setSpecifications(prev => [...prev, { key: '', value: '' }]);
  };

  const removeSpecification = (index: number) => {
    if (specifications.length > 1) {
      setSpecifications(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateSpecification = (index: number, field: 'key' | 'value', value: string) => {
    setSpecifications(prev => prev.map((spec, i) => 
      i === index ? { ...spec, [field]: value } : spec
    ));
  };

  // Upload images to Supabase Storage
  const uploadImages = async (): Promise<string[]> => {
    if (imageFiles.length === 0) return existingImages;

    setUploading(true);
    const uploadedUrls: string[] = [];

    try {
      for (const file of imageFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `product-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `products/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
        }

        const { data: { publicUrl } } = supabase.storage
          .from('product-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      return [...existingImages, ...uploadedUrls];
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (f: ProductForm) => {
      try {
        // Upload images first
        const imageUrls = await uploadImages();

        // Validate at least one image
        if (imageUrls.length === 0) {
          throw new Error('At least one product image is required.');
        }

        // Convert specifications array to object
        const specsObject: Record<string, string> = {};
        specifications.forEach(spec => {
          if (spec.key.trim() && spec.value.trim()) {
            specsObject[spec.key.trim()] = spec.value.trim();
          }
        });

        const payload = {
          name: f.name.trim(), 
          brand: f.brand.trim(), 
          category: f.category,
          price: Number(f.price), 
          original_price: Number(f.original_price),
          discount: Math.round(Number(f.discount)), 
          image: imageUrls[0],
          images: imageUrls,
          description: f.description.trim() || null, 
          in_stock: f.in_stock,
          is_flash_sale: f.is_flash_sale, 
          is_featured: f.is_featured,
          specs: specsObject,
          stock_quantity: Number(f.stock_quantity),
          low_stock_threshold: Number(f.low_stock_threshold),
          colors: f.colors ? f.colors.split(',').map(s => s.trim()).filter(Boolean) : [],
          storages: f.storages ? f.storages.split(',').map(s => s.trim()).filter(Boolean) : [],
          rams: f.rams ? f.rams.split(',').map(s => s.trim()).filter(Boolean) : [],
        };

        if (editId) {
          const { error } = await supabase.from('products').update(payload).eq('id', editId);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('products').insert(payload);
          if (error) throw error;
        }
      } catch (error: any) {
        console.error('Save product error:', error);
        
        // Provide specific error messages
        if (error.message?.includes('Bucket not found')) {
          throw new Error('Storage bucket not found. Please contact administrator to set up image storage.');
        } else if (error.message?.includes('upload')) {
          throw new Error('Failed to upload images. Please check your internet connection and try again.');
        } else if (error.message?.includes('permission')) {
          throw new Error('Permission denied. Please ensure you are logged in as admin.');
        } else {
          throw error;
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'products'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      toast({ title: editId ? 'Product updated successfully!' : 'Product created successfully!' });
      setIsOpen(false);
      setEditId(null);
      setForm(empty);
      setImageFiles([]);
      setImagePreviews([]);
      setExistingImages([]);
      setSpecifications([{ key: '', value: '' }]);
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error saving product', 
        description: error.message || 'An unexpected error occurred. Please try again.',
        variant: 'destructive' 
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'products'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      toast({ title: 'Product deleted successfully!' });
    },
    onError: (error: any) => {
      console.error('Delete product error:', error);
      
      // Check for foreign key constraint violation
      if (error.code === '23503' || error.message?.includes('foreign key') || error.message?.includes('violates')) {
        toast({
          title: 'Cannot delete product',
          description: 'This product has existing orders and cannot be deleted. Consider marking it as out of stock instead.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error deleting product',
          description: error.message || 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        });
      }
    },
  });

  const openEdit = (p: any) => {
    setEditId(p.id);
    setForm({
      name: p.name, brand: p.brand, category: p.category,
      price: String(p.price), original_price: String(p.original_price),
      discount: String(p.discount), description: p.description || '',
      in_stock: p.in_stock, is_flash_sale: p.is_flash_sale, is_featured: p.is_featured,
      stock_quantity: String(p.stock_quantity || 0),
      low_stock_threshold: String(p.low_stock_threshold || 5),
      colors: (p.colors || []).join(', '),
      storages: (p.storages || []).join(', '),
      rams: (p.rams || []).join(', '),
    });
    setExistingImages(p.images || []);
    
    // Load specifications
    const specs = p.specs || {};
    const specsArray = Object.entries(specs).map(([key, value]) => ({ key, value: String(value) }));
    setSpecifications(specsArray.length > 0 ? specsArray : [{ key: '', value: '' }]);
    
    setImageFiles([]);
    setImagePreviews([]);
    setIsOpen(true);
  };

  const resetDialog = () => {
    setEditId(null);
    setForm(empty);
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages([]);
    setSpecifications([{ key: '', value: '' }]);
  };

  const set = (key: keyof ProductForm, val: any) => setForm(prev => ({ ...prev, [key]: val }));

  const totalImages = existingImages.length + imageFiles.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold">Products ({products.length})</h1>
        <Dialog open={isOpen} onOpenChange={o => { setIsOpen(o); if (!o) resetDialog(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2"><Plus className="h-4 w-4" /> Add Product</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editId ? 'Edit' : 'Add'} Product</DialogTitle></DialogHeader>
            <form onSubmit={e => { e.preventDefault(); saveMutation.mutate(form); }} className="space-y-4">
              
              {/* Product Images Section */}
              <div className="space-y-3">
                <Label>Product Images (Min: 1, Max: 3)</Label>
                
                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {existingImages.map((url, i) => (
                      <div key={`existing-${i}`} className="relative group">
                        <img src={url} alt="" className="w-full h-32 object-cover rounded-lg border" />
                        {i === 0 && (
                          <div className="absolute top-1 left-1 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">
                            Main
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeExistingImage(i)}
                          className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Image Previews */}
                {imagePreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {imagePreviews.map((preview, i) => (
                      <div key={`preview-${i}`} className="relative group">
                        <img src={preview} alt="" className="w-full h-32 object-cover rounded-lg border border-dashed border-primary" />
                        {existingImages.length === 0 && i === 0 && (
                          <div className="absolute top-1 left-1 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">
                            Main
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeNewImage(i)}
                          className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                {totalImages < 3 && (
                  <Label htmlFor="product-images" className="cursor-pointer">
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                      <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium mb-1">Click to upload images</p>
                      <p className="text-xs text-muted-foreground">
                        JPG, PNG, or WebP (max 5MB each) • {totalImages}/3 uploaded
                      </p>
                    </div>
                    <input
                      id="product-images"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      multiple
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </Label>
                )}

                {totalImages === 0 && (
                  <p className="text-xs text-destructive">At least one image is required</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={form.name} onChange={e => set('name', e.target.value)} required /></div>
                <div><Label>Brand</Label><Input value={form.brand} onChange={e => set('brand', e.target.value)} required /></div>
              </div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => set('category', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="smartphones">Smartphones</SelectItem>
                    <SelectItem value="led-tvs">LED TVs</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                    <SelectItem value="appliances">Appliances</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={e => set('price', e.target.value)} required /></div>
                <div><Label>Original Price</Label><Input type="number" value={form.original_price} onChange={e => set('original_price', e.target.value)} required /></div>
                <div><Label>Discount %</Label><Input type="number" value={form.discount} onChange={e => set('discount', e.target.value)} /></div>
              </div>
              
              {/* Stock Management Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Stock Quantity</Label>
                  <Input 
                    type="number" 
                    min="0"
                    value={form.stock_quantity} 
                    onChange={e => set('stock_quantity', e.target.value)} 
                    required 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Current inventory count</p>
                </div>
                <div>
                  <Label>Low Stock Alert Threshold</Label>
                  <Input 
                    type="number" 
                    min="1"
                    value={form.low_stock_threshold} 
                    onChange={e => set('low_stock_threshold', e.target.value)} 
                    required 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Alert when stock falls below this number</p>
                </div>
              </div>
              
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} /></div>
              
              {/* Product Specifications Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Product Specifications</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSpecification}
                    className="gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add Spec
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {specifications.map((spec, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        placeholder="Specification (e.g., Brand, Display, RAM)"
                        value={spec.key}
                        onChange={e => updateSpecification(index, 'key', e.target.value)}
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">:</span>
                      <Input
                        placeholder="Value (e.g., Samsung, 6.5 inch, 8GB)"
                        value={spec.value}
                        onChange={e => updateSpecification(index, 'value', e.target.value)}
                        className="flex-1"
                      />
                      {specifications.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSpecification(index)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                {specifications.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Add product specifications like Brand, Model, Display Size, RAM, Storage, etc.
                  </p>
                )}
              </div>
              {/* Variants Section */}
              <div className="space-y-3 border rounded-lg p-3 bg-muted/20">
                <Label className="text-sm font-semibold">Product Variants (comma-separated)</Label>
                <div>
                  <Label className="text-xs text-muted-foreground">Colors</Label>
                  <Input placeholder="e.g. Star Silver, Cyber Green, Titanium Gold" value={form.colors} onChange={e => set('colors', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Storage Options</Label>
                  <Input placeholder="e.g. 128 GB, 256 GB, 512 GB" value={form.storages} onChange={e => set('storages', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">RAM Options</Label>
                  <Input placeholder="e.g. 6GB, 8GB, 12GB" value={form.rams} onChange={e => set('rams', e.target.value)} />
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex items-center gap-2"><Switch checked={form.in_stock} onCheckedChange={v => set('in_stock', v)} /><Label>In Stock</Label></div>                <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={v => set('is_featured', v)} /><Label>Featured</Label></div>
                <div className="flex items-center gap-2"><Switch checked={form.is_flash_sale} onCheckedChange={v => set('is_flash_sale', v)} /><Label>Flash Sale</Label></div>
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={saveMutation.isPending || uploading || totalImages === 0}
              >
                {saveMutation.isPending || uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {uploading ? 'Uploading images...' : 'Saving...'}
                  </>
                ) : (
                  editId ? 'Update Product' : 'Create Product'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card rounded-lg border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Sales</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading products...</TableCell></TableRow>
            ) : products.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No products yet. Click "Add Product" to create one.</TableCell></TableRow>
            ) : (
              products.map((p: any) => {
                const isLowStock = p.stock_quantity <= (p.low_stock_threshold || 5);
                const isOutOfStock = p.stock_quantity === 0;
                
                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.image && <img src={p.image} alt="" className="h-10 w-10 rounded object-cover" />}
                        <div><p className="font-medium text-sm">{p.name}</p><p className="text-xs text-muted-foreground">{p.brand}</p></div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm capitalize">{p.category}</TableCell>
                    <TableCell className="text-sm">₹{Number(p.price).toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <span className={`text-xs font-medium ${
                          isOutOfStock ? 'text-destructive' : 
                          isLowStock ? 'text-orange-600' : 'text-success'
                        }`}>
                          {isOutOfStock ? 'Out of Stock' : 
                           isLowStock ? `Low Stock (${p.stock_quantity})` : 
                           `${p.stock_quantity} units`}
                        </span>
                        {isLowStock && !isOutOfStock && (
                          <div className="text-xs text-muted-foreground">
                            Threshold: {p.low_stock_threshold || 5}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-medium">
                        {(p.total_sold || 0).toLocaleString('en-IN')} sold
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive" 
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete "${p.name}"? This action cannot be undone.`)) {
                            deleteMutation.mutate(p.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminProducts;
